<template>
  <div
    class="tw:flex tw:flex-col tw:h-full tw:flex-nowrap tw:w-full tw:overflow-x-hidden tw:min-h-0"
    :class="{ 'tw:overflow-y-auto': !props.paginate }"
  >
    <template v-if="isMobile && props.onNavBar">
      <RangeSelector
        v-if="!props.hideRangeSelector && isRangeSelectorActive"
        v-model:range="range"
        @update:range="onRangeUpdated"
      />
    </template>

    <div class="tw:shrink-0">
      <div
        v-if="!props.onNavBar && !props.hideButtonBar"
        class="tw:relative tw:flex card-column-header"
        :class="{ 'tw:h-12.5 tw:p-2.5': isMobile }"
      >
        <div
          class="mention-stream-title tw:flex tw:flex-1 tw:items-center tw:gap-1 tw:overflow-hidden tw:pr-1 tw:text-[15px] tw:font-bold"
          data-testid="title"
        >
          <slot name="title">
            <div
              v-if="props.stream.shared"
              class="tw:mr-0.75 tw:inline-block tw:size-3 tw:shrink-0 tw:bg-current"
              :style="sharedIconMaskStyle"
            />
            <BaseEllipseContent>
              {{ streamTitle }}
            </BaseEllipseContent>
          </slot>

          <slot name="resize" />
        </div>

        <slot name="header" />

        <MentionStreamHeaderToolbar
          v-model:range="range"
          v-model:range-selector-active="isRangeSelectorActive"
          :allow-all-time="props.allowAllTime"
          data-testid="mention-stream-header-toolbar"
          :filtering="filtering"
          :filters-selector-active="filtersSelectorActive"
          :group-slug="props.groupSlug"
          :hide-edit-button="props.hideEditButton"
          :hide-filters-selector="props.hideFiltersSelector"
          :hide-generate-report-button="props.hideGenerateReportButton"
          :hide-range-selector="props.hideRangeSelector"
          :index="props.index ?? 0"
          :stream="props.stream"
          @filters="activateSelector('filters')"
        >
          <template
            v-if="isDesktop"
            #prependButtonBar
          >
            <slot name="prependButtonBar" />

            <AISummaryButton
              v-if="isAISummarisationAllowed"
              data-intercom-target="mention-stream-ai-button"
              data-testid="summary-button"
              :data-walkthrough-id="`stream-${props.index}-header-toolbar-ai-button`"
              :stream-id="props.stream.id"
            />
          </template>
          <template #buttonBar>
            <slot name="buttonBar" />
          </template>
        </MentionStreamHeaderToolbar>
      </div>

      <AISummaryCard
        v-if="isAISummarisationAllowed"
        :stream-id="props.stream.id"
      />

      <div class="card-header tw:overflow-visible">
        <div
          v-if="loadingError"
          class="tw:bg-(--s-color-alert) tw:text-xs tw:shrink-0 tw:cursor-pointer tw:flex tw:gap-2 tw:p-2 tw:font-bold tw:items-center tw:justify-center tw:text-white"
          @click="onLoadingErrorRetry"
        >
          <BaseIcon icon="exclamation-circle" />
          {{ getLocaleText("mention_stream.error_loading_mentions") }}
        </div>

        <ExpandableFilters
          v-if="!props.hideFiltersSelector && props.stream"
          v-show="
            !props.hideFiltersSelector && props.stream && filtersSelectorActive
          "
          v-model:filter-set="filterSet"
          data-testid="expandable-filters"
          :enabled-media="props.enabledMedia"
          :filters="filters"
          :hide-advanced-filters="props.hideAdvancedFilters"
          show-apply
          :stick-filters="props.stickFilters"
          :stream="props.stream"
          @apply="onFilterApply"
          @reset="onFilterReset"
          @update:filter-set-id="onFilterSetApply"
        />

        <RangeSelector
          v-if="
            !props.onNavBar && !props.hideRangeSelector && isRangeSelectorActive
          "
          v-model:range="range"
          @update:range="onRangeUpdated"
        />

        <MentionStreamNotificationWatcher
          v-if="isNotificationWatcherVisible"
          :data-walkthrough-id="`stream-${props.index}-notification-watcher`"
          :stream="props.stream"
          @auto-update="autoRefreshMentions"
          @update="refreshMentions({ timestamp: $event })"
        />

        <MentionStreamExpiryWatcher
          v-if="isStreamExpiringSoon"
          :stream="props.stream"
        />

        <template v-if="filtering && !filtersSelectorActive">
          <slot
            :display-filters-selector="displayFiltersSelector"
            :filtering-label="filteringLabel"
            name="filtering-header"
          >
            <MentionStreamFilteringHeader
              :label="filteringLabel"
              @click="displayFiltersSelector"
            />
          </slot>
        </template>
      </div>

      <slot name="beforeMentions" />
    </div>

    <DefineTemplate>
      <SkeletonMentionStream v-if="isSkeletonMentionStreamVisible" />

      <MentionStreamList
        v-if="isActive && !isSkeletonMentionStreamVisible"
        :gutter-class="props.gutterClass"
        :index="props.index"
        :mentions="mentions"
        :no-highlighting="props.noHighlighting"
        :options="slimViewOptions"
        :range="range"
        :selected-composite-ids="props.selectedCompositeIds"
        :show-checkbox="props.showCheckbox"
        :show-more-content-option="isNoRangeSelectedMessageVisible"
        :stream="props.stream"
        @contact-clicked="handleOpenContactSidebar"
        @mention-clicked="mentionClicked"
        @mention-removed="removeMention"
        @mention-selected="onMentionSelected"
      >
        <template #emptyMentions>
          <slot name="noMentions" />
        </template>
      </MentionStreamList>

      <div
        v-if="isNoRangeSelectedMessageVisible && hasReachedEndOfMentions"
        class="tw:mt-4"
      >
        <SkeletonMentionStream v-if="loading && mentions.length" />
        <div
          v-if="!loading"
          :class="isMobile ? 'tw:mx-2 tw:my-4' : 'tw:ml-1'"
        >
          <div v-if="!mentions.length">
            <strong
              class="tw:mt-12 tw:mb-4 tw:flex tw:flex-col tw:items-center tw:justify-center"
            >
              {{ getLocaleText("mention_stream.no_media_items_found") }}
            </strong>
          </div>
          <div class="tw:text-denim-700 tw:mb-12 tw:text-center">
            <span v-if="!mentions.length">{{
              getLocaleText(
                "mention_stream_notification_watcher.no_media_items_since",
                {
                  date: formatIntlDate(range.after, { dateStyle: "long" }),
                }
              )
            }}</span>
            <span v-else>{{
              getLocaleText(
                "mention_stream_notification_watcher.media_items_since",
                {
                  date: formatIntlDate(range.after, { dateStyle: "long" }),
                }
              )
            }}</span>
            <div v-if="!hasReachedOneYear">
              <span
                class="bold dashed-underline pointer"
                @click="extendRange()"
              >
                {{
                  getLocaleText(
                    "mention_stream_notification_watcher.search_earlier_content_button"
                  )
                }}
              </span>
            </div>
          </div>
        </div>
      </div>
    </DefineTemplate>

    <QPullToRefresh
      v-if="isMobile && props.onNavBar && !props.paginate"
      @refresh="refreshMentions({ timestamp: Date.now() }, $event)"
    >
      <QInfiniteScroll
        ref="infiniteScroll"
        :class="infiniteScrollClass"
        :scroll-target="props.scrollTarget"
        @load="loadMore"
      >
        <ReuseTemplate />
      </QInfiniteScroll>
    </QPullToRefresh>

    <QInfiniteScroll
      v-else-if="!props.paginate"
      ref="infiniteScroll"
      :class="infiniteScrollClass"
      :scroll-target="props.scrollTarget"
      @load="loadMore"
    >
      <ReuseTemplate />

      <template
        v-if="!isNoRangeSelectedMessageVisible || !hasReachedEndOfMentions"
        #loading
      >
        <SkeletonMentionStream class="tw:mt-2" />
      </template>
    </QInfiniteScroll>

    <PaginatedStreamContainer
      v-else
      :hide-pagination-controls="props.hidePaginationControls"
      :loading="loading"
      :pagination="pagination"
      :show-skeleton="isSkeletonMentionStreamVisible"
      visible
      @pagination-changed="handlePaginationChange"
    >
      <ReuseTemplate />
    </PaginatedStreamContainer>
  </div>
</template>

<script setup lang="ts">
import { createReusableTemplate } from "@vueuse/core";
import { QInfiniteScroll, QPullToRefresh } from "quasar";
import { inject } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseEllipseContent } from "shared/components/base";
import BaseIcon from "shared/components/base/BaseIcon";
import PaginatedStreamContainer from "shared/components/PaginatedStreamContainer.vue";
import ExpandableFilters from "shared/components/selectors/ExpandableFilters.vue";
import RangeSelector from "shared/components/selectors/RangeSelector.vue";
import SkeletonMentionStream from "shared/components/SkeletonMentionStream.vue";
import AISummaryButton from "shared/modules/AISummary/components/AISummaryButton/AISummaryButton.vue";
import AISummaryCard from "shared/modules/AISummary/components/AISummaryCard/AISummaryCard.vue";
import MentionStreamExpiryWatcher from "shared/modules/MentionStream/components/MentionStreamExpiryWatcher/MentionStreamExpiryWatcher.vue";
import MentionStreamFilteringHeader from "shared/modules/MentionStream/components/MentionStreamFilteringHeader/MentionStreamFilteringHeader.vue";
import MentionStreamHeaderToolbar from "shared/modules/MentionStream/components/MentionStreamHeaderToolbar/MentionStreamHeaderToolbar.vue";
import MentionStreamList from "shared/modules/MentionStream/components/MentionStreamList/MentionStreamList.vue";
import MentionStreamNotificationWatcher from "shared/modules/MentionStream/components/MentionStreamNotificationWatcher/MentionStreamNotificationWatcher.vue";
import { useMentionStream } from "shared/modules/MentionStream/composables/useMentionStream";
import {
  type MentionStreamEmits,
  type MentionStreamProps,
} from "shared/modules/MentionStream/types";

const [DefineTemplate, ReuseTemplate] = createReusableTemplate();

const props = defineProps<MentionStreamProps>();
const emit = defineEmits<MentionStreamEmits>();

const isDesktop = inject<boolean>("isDesktop");
const isMobile = inject<boolean>("isMobile");

const sharedIconMaskStyle = {
  mask: `url(${import.meta.env.VITE_ASSETS_URL}/icons/menu/menu-stream-share.svg) center / cover no-repeat`,
};

const {
  activateSelector,
  autoRefreshMentions,
  displayFiltersSelector,
  extendRange,
  filtering,
  filteringLabel,
  filters,
  filterSet,
  filtersSelectorActive,
  formatIntlDate,
  handleOpenContactSidebar,
  handlePaginationChange,
  hasReachedOneYear,
  infiniteScroll,
  infiniteScrollClass,
  isActive,
  isAISummarisationAllowed,
  isNoRangeSelectedMessageVisible,
  isNotificationWatcherVisible,
  isRangeSelectorActive,
  isSkeletonMentionStreamVisible,
  isStreamExpiringSoon,
  loading,
  loadingError,
  loadMentions,
  loadMore,
  mentionClicked,
  mentions,
  onFilterApply,
  onFilterReset,
  onFilterSetApply,
  onLoadingErrorRetry,
  onMentionSelected,
  onRangeUpdated,
  pagination,
  range,
  hasReachedEndOfMentions,
  refresh,
  refreshMentions,
  removeMention,
  slimViewOptions,
  streamTitle,
} = useMentionStream(props, { emit });

defineExpose({
  activateSelector,
  filtering,
  filters,
  filtersSelectorActive,
  loadMentions,
  refresh,
  refreshMentions,
});
</script>

<style lang="scss" scoped>
.card-header {
  border-bottom: 0;

  > * {
    border-bottom: 1px solid $hover-background;
  }
}
</style>
